import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from '../Modals/Modal';
import toast from 'react-hot-toast';

const AddJobModal = ({ 
  isOpen, 
  onClose, 
  defWorkId, 
  user, 
  onJobAdded, 
  isAdmin = false,
  initialProjectId = null,      
  initialProjectName = ''       
}) => {
    // ----- Form fields -----
    const [newJobName, setNewJobName] = useState('');
    const [newJobDescription, setNewJobDescription] = useState('');
    const [newJobStatus, setNewJobStatus] = useState(1);
    const [newJobType, setNewJobType] = useState('');
    const [newJobPriority, setNewJobPriority] = useState(null);
    const [newJobStartDate, setNewJobStartDate] = useState('');
    const [newJobEndDate, setNewJobEndDate] = useState('');
    const [newJobActualEndDate, setNewJobActualEndDate] = useState('');
    const [newJobManagerId, setNewJobManagerId] = useState(null);
    const [singleSelectedProject, setSingleSelectedProject] = useState('');
    const [bulkSelectedProject, setBulkSelectedProject] = useState('');
    const [activeTab, setActiveTab] = useState('single');
    const [pasteContent, setPasteContent] = useState('');
    const [parsedJobs, setParsedJobs] = useState([]);
    const [isReviewMode, setIsReviewMode] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);
    const [isAdding, setIsAdding] = useState(false);
    const [modalProjectError, setModalProjectError] = useState(false);
    
    // ----- Data states -----
    const [allProjects, setAllProjects] = useState([]);
    const [workspaceUsers, setWorkspaceUsers] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);
    const [projectJobs, setProjectJobs] = useState([]);
    const [loadingProjectJobs, setLoadingProjectJobs] = useState(false);
    const [showJobDropdown, setShowJobDropdown] = useState(false);
    const [jobNameSearch, setJobNameSearch] = useState('');
    
    // Search states
    const [singleProjectSearchTerm, setSingleProjectSearchTerm] = useState('');
    const [singleSearchedProjects, setSingleSearchedProjects] = useState([]);
    const [singleProjectLoading, setSingleProjectLoading] = useState(false);
    const [bulkProjectSearchTerm, setBulkProjectSearchTerm] = useState('');
    const [bulkSearchedProjects, setBulkSearchedProjects] = useState([]);
    const [bulkProjectLoading, setBulkProjectLoading] = useState(false);
    
    const errorRef = useRef(null);
    const tableRef = useRef(null);
    const modalContentRef = useRef(null);
    const addTopRef = useRef(null);
    const singleDebounceRef = useRef(null);
    const bulkDebounceRef = useRef(null);
    
    // Keyboard shortcut Ctrl+S
    const handleKeyDown = useCallback((event) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 's') {
            event.preventDefault();
            if (!isAdding) {
                if (activeTab === 'single' && singleSelectedProject && newJobName.trim()) {
                    handleAddJob();
                } else if (activeTab === 'bulk' && isReviewMode && bulkSelectedProject && parsedJobs.length > 0) {
                    handleAddBulkJobs();
                }
            }
        }
    }, [isAdding, activeTab, singleSelectedProject, newJobName, bulkSelectedProject, isReviewMode, parsedJobs]);
    
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);
    
    // Reset and load data when modal opens
    useEffect(() => {
        if (isOpen) {
            setProjectsLoading(true);
            setUsersLoading(true);
            loadAllProjects();
            loadWorkspaceUsers();
            resetFormStates();
            setErrorMessages([]);
        }
    }, [isOpen, defWorkId, user]);

    // After projects load, pre-select the initial project if provided
    useEffect(() => {
        if (allProjects.length > 0 && initialProjectId && !singleSelectedProject) {
            const projectExists = allProjects.some(p => p.id === parseInt(initialProjectId));
            if (projectExists) {
                setSingleSelectedProject(initialProjectId);
                fetchProjectJobs(initialProjectId);
            }
        }
    }, [allProjects, initialProjectId, singleSelectedProject]);

    // Scroll to top when adding
    useEffect(() => {
        if (isAdding && addTopRef.current) {
            addTopRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
    }, [isAdding]);
    
    // Debounced project search for single tab
    useEffect(() => {
        if (singleDebounceRef.current) clearTimeout(singleDebounceRef.current);
        if (!singleProjectSearchTerm.trim()) return;
        singleDebounceRef.current = setTimeout(() => {
            loadSearchedProjects(singleProjectSearchTerm, setSingleSearchedProjects, setSingleProjectLoading);
        }, 300);
        return () => clearTimeout(singleDebounceRef.current);
    }, [singleProjectSearchTerm]);
    
    // Debounced project search for bulk tab
    useEffect(() => {
        if (bulkDebounceRef.current) clearTimeout(bulkDebounceRef.current);
        if (!bulkProjectSearchTerm.trim()) return;
        bulkDebounceRef.current = setTimeout(() => {
            loadSearchedProjects(bulkProjectSearchTerm, setBulkSearchedProjects, setBulkProjectLoading);
        }, 300);
        return () => clearTimeout(bulkDebounceRef.current);
    }, [bulkProjectSearchTerm]);
    
    const fetchProjectJobs = async (projectId) => {
        if (!projectId || !user) return;
        setLoadingProjectJobs(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/UserJobAuth/GetJobsByUserAndProject/${user}/${projectId}`
            );
            if (response.ok) {
                const data = await response.json();
                setProjectJobs(data.jobs || []);
            } else {
                setProjectJobs([]);
            }
        } catch {
            setProjectJobs([]);
            toast.error('Failed to load project jobs');
        } finally {
            setLoadingProjectJobs(false);
        }
    };
    
    const loadAllProjects = async () => {
        setModalProjectError(false);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/Project/GetProjectsByUserJobPermission/${user}/${defWorkId}`
            );
            if (res.ok) {
                const data = await res.json();
                setAllProjects(data.projects || []);
            } else {
                throw new Error();
            }
        } catch {
            setModalProjectError(true);
            toast.error('Failed to load projects');
        } finally {
            setProjectsLoading(false);
        }
    };
    
    const loadWorkspaceUsers = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/UserWorkspace/GetUsersByWorkspaceId/${defWorkId}`);
            if (res.ok) {
                const data = await res.json();
                setWorkspaceUsers(data.users || []);
            } else {
                throw new Error();
            }
        } catch {
            toast.error('Failed to load users');
        } finally {
            setUsersLoading(false);
        }
    };
    
    const loadSearchedProjects = async (search = '', setSearched, setLoading) => {
        if (!search.trim()) {
            setSearched([]);
            return;
        }
        setLoading(true);
        setModalProjectError(false);
        try {
            const params = new URLSearchParams();
            params.append('q', search);
            params.append('userId', user);
            if (defWorkId) params.append('workspaceId', defWorkId);
            const url = `${process.env.REACT_APP_API_BASE_URL}/api/SiteNote/SearchProjects?${params.toString()}`;
            const res = await fetch(url);
            if (res.ok) {
                const jsonData = await res.json();
                const rawData = jsonData.results || [];
                const normalizedProjects = rawData.map(p => ({
                    id: p.projectId,
                    name: p.projectName,
                    workspaceId: p.workspaceId,
                    status: 1,
                    fullPath: p.fullPath
                }));
                const uniqueProjects = Array.from(new Map(normalizedProjects.map(p => [p.id, p])).values());
                setSearched(uniqueProjects);
            } else {
                throw new Error();
            }
        } catch {
            setModalProjectError(true);
            toast.error('Failed to load projects');
        } finally {
            setLoading(false);
        }
    };
    
    const grantJobPermission = async (jobid) => {
        try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/UserJobAuth/AddUserJob`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: user, jobId: jobid, userIDScreen: user })
            });
        } catch { }
    };
    
    const handleAddJob = async () => {
        setIsAdding(true);
        setErrorMessages([]);
        try {
            const bodyData = {
                name: newJobName.trim(),
                description: newJobDescription.trim(),
                status: newJobStatus,
                projectId: singleSelectedProject,
                userId: user
            };
            if (newJobType.trim()) bodyData.type = newJobType.trim();
            if (newJobPriority !== null) bodyData.jobPriority = newJobPriority;
            if (newJobStartDate) bodyData.startDate = new Date(newJobStartDate).toISOString();
            if (newJobEndDate) bodyData.endDate = new Date(newJobEndDate).toISOString();
            if (newJobActualEndDate) bodyData.actualEndDate = new Date(newJobActualEndDate).toISOString();
            if (newJobManagerId !== null) bodyData.managerId = newJobManagerId;
            
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/Job/AddJob`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData)
            });
            if (!response.ok) {
                const errorData = await response.json();
                let errors = [];
                if (Array.isArray(errorData.errors)) errors = errorData.errors;
                else if (errorData.errors && typeof errorData.errors === 'object') {
                    Object.values(errorData.errors).flat().forEach(msg => errors.push(msg));
                } else if (errorData.message) errors = [errorData.message];
                setErrorMessages(errors);
                if (errorRef.current) errorRef.current.scrollIntoView({ behavior: 'auto' });
                return;
            }
            const data = await response.json();
            toast.success('Job saved successfully');
            await grantJobPermission(data.job.id);
            if (onJobAdded) onJobAdded();
            onClose();
        } catch {
            toast.error('Network error');
        } finally {
            setIsAdding(false);
        }
    };
    
    const handleAddBulkJobs = async () => {
        setIsAdding(true);
        setErrorMessages([]);
        if (!bulkSelectedProject || parsedJobs.length === 0) {
            setErrorMessages(['Project and jobs required']);
            setIsAdding(false);
            return;
        }
        const dtos = parsedJobs.map(job => ({
            name: job.name.trim(),
            description: job.description?.trim() || job.name.trim(),
            projectId: parseInt(bulkSelectedProject),
            userId: user
        }));
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/Job/AddJobsBulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dtos)
            });
            if (!response.ok) {
                const err = await response.json();
                let errors = [];
                if (Array.isArray(err.errors)) errors = err.errors;
                else if (err.errors) errors = Object.values(err.errors).flat();
                else if (err.message) errors = [err.message];
                setErrorMessages(errors);
                return;
            }
            const data = await response.json();
            toast.success(`${data.jobs.length} jobs added`);
            const grantPromises = data.jobs.map(job => grantJobPermission(job.id));
            await Promise.all(grantPromises);
            if (onJobAdded) onJobAdded();
            onClose();
        } catch {
            toast.error('Network error');
        } finally {
            setIsAdding(false);
        }
    };
    
    const resetFormStates = () => {
        setNewJobName('');
        setNewJobDescription('');
        setNewJobType('');
        setNewJobPriority(null);
        setNewJobStartDate('');
        setNewJobEndDate('');
        setNewJobActualEndDate('');
        setNewJobManagerId(null);
        setNewJobStatus(1);
        // DO NOT reset singleSelectedProject if we have initialProjectId?
        // The modal will re-open with new initialProjectId, so we clear it now.
        // But the effect will set it again after projects load.
        setSingleSelectedProject('');
        setBulkSelectedProject('');
        setPasteContent('');
        setParsedJobs([]);
        setIsReviewMode(false);
        setActiveTab('single');
        setSingleProjectSearchTerm('');
        setSingleSearchedProjects([]);
        setBulkProjectSearchTerm('');
        setBulkSearchedProjects([]);
        setProjectJobs([]);
        setShowJobDropdown(false);
        setJobNameSearch('');
    };
    
    const handlePasteChange = (e) => setPasteContent(e.target.value);
    
    const handleReview = () => {
        const lines = pasteContent.trim().split('\n');
        const newParsed = lines
            .map(line => {
                const [name, description] = line.split('\t').map(str => str.trim());
                if (name) return { name, description: description || '' };
                return null;
            })
            .filter(Boolean);
        setParsedJobs(newParsed);
        setIsReviewMode(true);
    };
    
    const handleEditParsedJob = (index, field, value) => {
        const updated = [...parsedJobs];
        updated[index][field] = value;
        setParsedJobs(updated);
    };
    
    const handleDeleteRow = (index) => {
        setParsedJobs(parsedJobs.filter((_, i) => i !== index));
    };
    
    const handleAddNewRow = () => {
        setParsedJobs([...parsedJobs, { name: '', description: '' }]);
        setTimeout(() => {
            if (tableRef.current) {
                tableRef.current.scrollTop = tableRef.current.scrollHeight;
            }
        }, 0);
    };
    
    const handleClearPaste = () => {
        setPasteContent('');
        setParsedJobs([]);
        setIsReviewMode(false);
    };
    
    const handleSingleSearchChange = (e) => {
        const newTerm = e.target.value;
        setSingleSearchedProjects([]);
        setSingleProjectLoading(!!newTerm.trim());
        setSingleProjectSearchTerm(newTerm);
    };
    
    const handleBulkSearchChange = (e) => {
        const newTerm = e.target.value;
        setBulkSearchedProjects([]);
        setBulkProjectLoading(!!newTerm.trim());
        setBulkProjectSearchTerm(newTerm);
    };
    
    const activeProjects = allProjects; // already filtered by user/workspace
    
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add Job" customClass="modal-md">
            <div ref={modalContentRef} style={{ padding: '20px', position: 'relative' }}>
                <div ref={addTopRef}></div>
                {(isAdding || projectsLoading || usersLoading) && (
                    <div className="full-overlay">
                        <div className="spinner" />
                        <p style={{ fontSize: '1.2rem', fontWeight: '600', margin: 0 }}>
                            {projectsLoading ? 'Loading projects...' : usersLoading ? 'Loading users...' : 
                             activeTab === 'bulk' ? `Adding ${parsedJobs.length} job${parsedJobs.length !== 1 ? 's' : ''}...` : 'Adding job...'}
                        </p>
                    </div>
                )}
                <div style={{ opacity: isAdding ? 0.4 : 1, pointerEvents: isAdding ? 'none' : 'all' }}>
                    <div ref={errorRef} style={{ marginBottom: '15px' }}>
                        {errorMessages.length > 0 && (
                            <div className="error-message">
                                <ul style={{ color: '#d32f2f', margin: '8px 0' }}>
                                    {errorMessages.map((msg, i) => <li key={i}>{msg}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                    <div className="tab-container" style={{ marginBottom: '20px' }}>
                        <button className={`tab-button ${activeTab === 'single' ? 'active' : ''}`} onClick={() => setActiveTab('single')}>
                            Single Add
                        </button>
                        {isAdmin && (
                            <button className={`tab-button ${activeTab === 'bulk' ? 'active' : ''}`} onClick={() => setActiveTab('bulk')}>
                                Bulk Add
                            </button>
                        )}
                    </div>
                    {activeTab === 'single' && (
                        <div className="form-container">
                            <div className="form-group full-width">
                                <label>Project Search:</label>
                                <div className="search-wrapper">
                                    <input
                                        type="text"
                                        value={singleProjectSearchTerm}
                                        onChange={handleSingleSearchChange}
                                        placeholder="Search projects..."
                                        style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                                    />
                                    {singleProjectLoading && <div className="dropdown-message">Loading...</div>}
                                    {!singleProjectLoading && singleProjectSearchTerm.trim() && singleSearchedProjects.length === 0 && (
                                        <div className="dropdown-message error-box">No projects match your search</div>
                                    )}
                                    {!singleProjectLoading && singleSearchedProjects.length > 0 && (
                                        <ul className="project-search-list">
                                            {singleSearchedProjects.map(p => (
                                                <li key={p.id} onClick={() => {
                                                    setSingleSelectedProject(p.id);
                                                    setSingleProjectSearchTerm('');
                                                    setSingleSearchedProjects([]);
                                                    fetchProjectJobs(p.id);
                                                }}>
                                                    {p.fullPath}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Project:</label>
                                {modalProjectError ? (
                                    <div className="error-box">Failed to load projects</div>
                                ) : activeProjects.length === 0 ? (
                                    <div className="error-box">No projects available</div>
                                ) : (
                                    <select value={singleSelectedProject} onChange={(e) => {
                                        setSingleSelectedProject(e.target.value);
                                        fetchProjectJobs(e.target.value);
                                    }}>
                                        <option value="">Select Project</option>
                                        {activeProjects.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Job Name:</label>
                                <div className="job-name-search-wrapper">
                                    <input
                                        type="text"
                                        value={newJobName}
                                        onChange={(e) => {
                                            setNewJobName(e.target.value);
                                            setJobNameSearch(e.target.value);
                                            setShowJobDropdown(true);
                                        }}
                                        onFocus={() => setShowJobDropdown(true)}
                                        placeholder="Enter job name or click to see existing jobs"
                                        autoComplete="off"
                                        style={{ width: '100%' }}
                                    />
                                    {showJobDropdown && loadingProjectJobs && <div className="dropdown-message">Loading jobs...</div>}
                                    {showJobDropdown && !loadingProjectJobs && projectJobs.length > 0 && (
                                        <ul className="project-search-list" style={{ marginTop: '2px', maxHeight: '150px', overflowY: 'auto' }}>
                                            {projectJobs.filter(job => job.jobName.toLowerCase().includes(jobNameSearch.toLowerCase())).map(job => (
                                                <li key={job.jobId} onClick={() => {
                                                    setNewJobName(job.jobName);
                                                    setShowJobDropdown(false);
                                                }} style={{ cursor: 'pointer', padding: '8px 12px' }}>
                                                    {job.jobName}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {showJobDropdown && !loadingProjectJobs && projectJobs.length === 0 && singleSelectedProject && (
                                        <div className="dropdown-message">No existing jobs found for this project</div>
                                    )}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Status:</label>
                                <select value={newJobStatus} onChange={e => setNewJobStatus(parseInt(e.target.value))}>
                                    <option value={1}>Active</option>
                                    <option value={2}>Inactive</option>
                                    <option value={3}>Archive</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Type:</label>
                                <input type="text" value={newJobType} onChange={e => setNewJobType(e.target.value)} placeholder="Enter job type" />
                            </div>
                            <div className="form-group">
                                <label>Priority:</label>
                                <select value={newJobPriority ?? ''} onChange={e => setNewJobPriority(e.target.value === '' ? null : parseInt(e.target.value))}>
                                    <option value="">Select Priority</option>
                                    <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
                                    <option value={4}>4</option><option value={5}>5</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Start Date:</label>
                                <input type="date" value={newJobStartDate} onChange={e => setNewJobStartDate(e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label>End Date:</label>
                                <input type="date" value={newJobEndDate} onChange={e => setNewJobEndDate(e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label>Actual End Date:</label>
                                <input type="date" value={newJobActualEndDate} onChange={e => setNewJobActualEndDate(e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label>Manager:</label>
                                <select value={newJobManagerId ?? ''} onChange={e => setNewJobManagerId(e.target.value === '' ? null : parseInt(e.target.value))}>
                                    <option value="">Select Manager</option>
                                    {workspaceUsers.map(u => (
                                        <option key={u.userId} value={u.userId}>{`${u.fname} ${u.lname} (${u.email})`}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group full-width">
                                <label>Job Description:</label>
                                <textarea value={newJobDescription} onChange={e => setNewJobDescription(e.target.value)} placeholder="Enter job description" />
                            </div>
                        </div>
                    )}
                    {activeTab === 'bulk' && (
                        <>
                            {!isReviewMode ? (
                                <>
                                    <div className="form-group">
                                        <label>Project Search:</label>
                                        <div className="search-wrapper">
                                            <input type="text" value={bulkProjectSearchTerm} onChange={handleBulkSearchChange} placeholder="Search projects..." style={{ width: '100%', padding: '8px' }} />
                                            {bulkProjectLoading && <div className="dropdown-message">Loading...</div>}
                                            {!bulkProjectLoading && bulkProjectSearchTerm.trim() && bulkSearchedProjects.length === 0 && (
                                                <div className="dropdown-message error-box">No projects match your search</div>
                                            )}
                                            {!bulkProjectLoading && bulkSearchedProjects.length > 0 && (
                                                <ul className="project-search-list">
                                                    {bulkSearchedProjects.map(p => (
                                                        <li key={p.id} onClick={() => {
                                                            setBulkSelectedProject(p.id);
                                                            setBulkProjectSearchTerm('');
                                                            setBulkSearchedProjects([]);
                                                        }}>
                                                            {p.fullPath}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Project:</label>
                                        {modalProjectError ? (
                                            <div className="error-box">Failed to load projects</div>
                                        ) : activeProjects.length === 0 ? (
                                            <div className="error-box">No projects available</div>
                                        ) : (
                                            <select value={bulkSelectedProject} onChange={e => setBulkSelectedProject(e.target.value)}>
                                                <option value="">Select Project</option>
                                                {activeProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </select>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label>Paste from Excel (Name[Tab]Description per line):</label>
                                        <textarea value={pasteContent} onChange={handlePasteChange} placeholder="Job A\tDescription A\nJob B\tDescription B" className="modern-textarea" style={{ minHeight: '180px' }} />
                                    </div>
                                    <div style={{ textAlign: 'center', margin: '20px 0' }}>
                                        <button onClick={handleReview} className="btn-primary" disabled={!pasteContent.trim()}>Review</button>
                                        <button onClick={handleClearPaste} className="btn-danger" style={{ marginLeft: '10px' }}>Clear</button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h4 style={{ textAlign: 'center', margin: '15px 0' }}>Review Jobs ({parsedJobs.length})</h4>
                                    <div ref={tableRef} style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '8px' }}>
                                        <table className="modern-table">
                                            <thead>
                                                <tr><th>Name</th><th>Description</th><th>Action</th></tr>
                                            </thead>
                                            <tbody>
                                                {parsedJobs.map((job, idx) => (
                                                    <tr key={idx}>
                                                        <td><input value={job.name} onChange={e => handleEditParsedJob(idx, 'name', e.target.value)} className="modern-input" /></td>
                                                        <td><input value={job.description} onChange={e => handleEditParsedJob(idx, 'description', e.target.value)} className="modern-input" /></td>
                                                        <td><button onClick={() => handleDeleteRow(idx)} className="icon-button danger"><i className="fas fa-trash"></i></button></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div style={{ marginTop: '15px', textAlign: 'center' }}>
                                        <button onClick={handleAddNewRow} className="icon-button secondary">Add Row</button>
                                        <button onClick={handleAddBulkJobs} className="btn-primary" style={{ marginLeft: '12px' }}>Add All Jobs</button>
                                        <button onClick={() => setIsReviewMode(false)} className="btn-secondary" style={{ marginLeft: '12px' }}>Back</button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    <div className="modal-footer" style={{ marginTop: '30px', gridColumn: 'span 2' }}>
                        {activeTab === 'single' && (
                            <button className="btn-primary" onClick={handleAddJob} disabled={!singleSelectedProject || !newJobName.trim() || isAdding}>
                                {isAdding ? 'Adding...' : 'OK'}
                            </button>
                        )}
                        {activeTab === 'bulk' && isReviewMode && (
                            <button className="btn-primary" onClick={handleAddBulkJobs} disabled={!bulkSelectedProject || parsedJobs.length === 0 || isAdding}>
                                {isAdding ? 'Adding...' : 'OK'}
                            </button>
                        )}
                        <button className="btn-close" onClick={onClose}>Cancel</button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AddJobModal;